/**
 * @citeclerk/ui — THE AUTHORITY CARD. One component, both surfaces.
 *
 * ── WHY THIS EXISTS ──────────────────────────────────────────────────────────────────────────
 * The UI audit compared Clark's `.cc-ans-card` against Research's `.wk-row` property by property
 * and found **zero** in common: not the width, height, radius, padding, border, shadow, heading
 * CONTENT (Clark led with the full Bluebook citation; Research with the short case name), heading
 * type, badge, court, cited-by count, selection, or click behaviour. Two independent
 * implementations of one idea, which is the shape that produced the ge5 defect this family already
 * paid for once: the composer saying "not yet analyzed" while the authorities panel said "no
 * negative treatment detected", about the same case, in the same response.
 *
 * So this is an extraction into `packages/ui`, where the shared header and the shared composer
 * already live, rather than a third card. Both hosts render THIS.
 *
 * ── THE SEVEN ELEMENTS, AND WHERE EACH CAME FROM ─────────────────────────────────────────────
 *   type badge        ← Clark        `type`
 *   why-relied-on     ← Clark        `why`
 *   case NAME         ← Research     `name`     (Clark led with the citation; a citation is an
 *                                                address, and you do not recognise a case by its
 *                                                address)
 *   court             ← Research     `court`
 *   cited-by count    ← Research     `citedBy`
 *   selection state   ← Research     `selected`
 *   click-through     ← Research     `href` / `onSelect`
 *
 * ── 🔴 WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────────────────────
 * THERE IS NO TREATMENT / GOOD-LAW SLOT, and adding one back is not a small change. Both cards
 * carried one (Clark's `.cc-tx-pill`, Research's `.wk-st--cau/ok/neg`) and this bundle removes
 * every citator surface from the reading path. A card that can display a verdict is a card that
 * can disagree with the other one about it, which is the entire reason this file exists.
 *
 * ── 🔴 A SLOT WITH NO DATA RENDERS NOTHING, AND THAT IS NOT A BUG ────────────────────────────
 * Every field is optional and every one is guarded. Research today has no honest "why this was
 * relied on": its answer payload carries `matchedChunkText`, a raw retrieved chunk that may be
 * about something else entirely, and Clark's own shape module rejected exactly that substitution
 * (server/lib/clarkAnswerShape.js:100 — "The honest replacement is not `matchedChunkText`"). So
 * Research passes no `why` and the line does not render. Filling it needs a field from the
 * ANSWER path, which is a different stream's file. Same for a card with no cluster id: it renders
 * every fact it has and does not become a link to the wrong case.
 *
 * ── LEGACY CLASS PASSTHROUGH ─────────────────────────────────────────────────────────────────
 * `legacyClass` puts the host's original class on the root ALONGSIDE `cc-auth`. This is not
 * decoration: `apps/steel/scripts/smoke.mjs` and several suites select `.wk-row`, and Workspace's
 * list is a `role="listbox"` whose options are found by class. Renaming them in the same commit
 * that changes the markup would make a rendering change indistinguishable from a selector break.
 * The visual rules all hang off `cc-auth`; the legacy name carries no styling of its own here.
 */
import React from 'react';
import './AuthorityCard.css';

/**
 * @param {object}   p
 * @param {string}   [p.name]      short case name — the heading. Falls back to `cite`.
 * @param {string}   [p.cite]      the full Bluebook citation — the meta line, under the name.
 * @param {string}   [p.type]      'Case' | 'Statute' | … — the badge.
 * @param {string}   [p.court]     court, in the meta line.
 * @param {string|number} [p.year] year, in the meta line.
 * @param {number}   [p.citedBy]   citing-opinion count.
 * @param {string}   [p.status]    RUN S · 1.4 — a STORED status about the authority itself, today
 *                                 only a statute's `repealed` / `transferred` / `omitted` /
 *                                 `renumbered`. See the block above `statusChip` below for why this
 *                                 is not the treatment slot that was deliberately removed.
 * @param {string}   [p.why]       one sentence: what this authority established FOR THIS ANSWER.
 * @param {string}   [p.href]      when set, the whole card is an <a> (middle-click, copy-link work).
 * @param {Function} [p.onSelect]  click handler. With `href`, it preventDefaults and takes over.
 * @param {boolean}  [p.selected]  the selection state Research had and Clark did not.
 * @param {string}   [p.as]        'li' (default) | 'div' — the element the host's list needs.
 * @param {string}   [p.legacyClass] host's pre-existing class, kept on the root. See the header.
 * @param {string}   [p.unlinkedNote] shown when there is no href — never a dead link.
 */
/**
 * ── 🔴 RUN S · 1.4 — `status` IS NOT THE TREATMENT SLOT COMING BACK. ─────────────────────────────
 *
 * The header above says there is no treatment / good-law slot, and that is still true. The
 * distinction is what produces the value, and it is the same distinction that let "Most cited for"
 * survive the citator's removal while "Strongest treatment" did not:
 *
 *   a TREATMENT verdict is an INFERENCE — this court's language about that case, classified by us.
 *     Two surfaces computing it independently can disagree about the same case in the same response,
 *     which is the ge5 defect this component exists to prevent.
 *   `status` is a STORED COLUMN — `statute_sections.status`, written at ingest, read not derived.
 *     Two surfaces printing it cannot disagree unless one of them is reading the wrong row.
 *
 * 🔴 AND IT IS ONE-SIDED, WHICH THE RENDERING HAS TO HONOUR. RUN J established that a state
 * statute's flag can honestly say 'repealed' or nothing and NEVER 'in force' — 2,691 rows whose own
 * text begins "Repealed…" carry is_repealed = false. So an ABSENT status renders as ABSENT: there is
 * no "in force" chip, no green tick, and no styling that could be read as a clean bill of health.
 * Only a positive, named, adverse status draws anything at all.
 */
const STATUS_LABEL = {
  repealed: 'Repealed',
  transferred: 'Transferred',
  omitted: 'Omitted',
  renumbered: 'Renumbered',
};

export default function AuthorityCard({
  name, cite, type, court, year, citedBy, why, status = null,
  href, onSelect, selected = false,
  as: Tag = 'li',
  legacyClass = '',
  unlinkedNote = null,
  testId = 'authority-card',
  // 🔴 WHO OWNS THE CLICK. Not cosmetic — it decides which element is interactive, and getting it
  // wrong breaks either the keyboard or the listbox.
  //   'auto'      (default) — an <a> if `href`, else a <button> if `onSelect`, else inert. Clark.
  //   'container' — the ROOT is the control and the body is a plain div. Research's authorities
  //                 pane is a `role="listbox"` whose rows are `role="option"`, and an option may
  //                 not contain a button: the row would be announced as a control inside a control
  //                 and arrow-key navigation would fight the inner tab stop.
  interactive = 'auto',
  // Rendered as a SIBLING of the hit area, never inside it — Library's row carries its own
  // buttons (Open / + Matter / Remove) and nesting a button in a button is invalid markup that
  // browsers resolve by dropping one of the two click targets.
  footer = null,
  ...rest
}) {
  // The heading is the NAME; the citation is the address underneath it. When there is no name —
  // a statute row, or a case the engine returned uncaptioned — the citation is promoted rather
  // than leaving an empty heading and an orphaned meta line.
  const heading = name || cite || '—';
  const metaParts = [name ? cite : null, court, year].filter(Boolean);
  /* Only a KNOWN adverse status renders. An unrecognised string draws nothing rather than being
     printed raw: a chip reading "status: null" or an untranslated enum on a legal surface is worse
     than silence, and silence is the correct reading of "we hold no adverse status for this". */
  const statusLabel = status ? STATUS_LABEL[String(status).toLowerCase()] || null : null;

  const body = (
    <>
      {type || statusLabel ? (
        <span className="cc-auth__typeline">
          {type ? <span className="cc-auth__type">{type}</span> : null}
          {statusLabel ? (
            <span className="cc-auth__status" data-status={String(status).toLowerCase()}
                  title={`This provision is marked ${statusLabel.toLowerCase()} in the source we hold. Any currency date below describes the text we store, not whether it is in force.`}>
              {statusLabel}
            </span>
          ) : null}
        </span>
      ) : null}
      <span className="cc-auth__nm">{heading}</span>
      {metaParts.length ? <span className="cc-auth__meta">{metaParts.join(' · ')}</span> : null}
      {Number.isFinite(citedBy) && citedBy > 0 ? (
        <span className="cc-auth__sig" title="Times cited by other opinions">
          Cited by {citedBy.toLocaleString()}
        </span>
      ) : null}
      {why ? <span className="cc-auth__why">{why}</span> : null}
    </>
  );

  const cls = `cc-auth${selected ? ' is-selected' : ''}${legacyClass ? ` ${legacyClass}` : ''}`;

  // An <a> when there is somewhere to go, a <button> when the host owns navigation itself, and a
  // plain <div> when neither — never a clickable box with no role.
  let hit;
  if (interactive === 'container') {
    hit = <div className="cc-auth__hit is-passive">{body}</div>;
  } else if (href) {
    hit = (
      <a
        className="cc-auth__hit"
        href={href}
        aria-current={selected ? 'true' : undefined}
        onClick={(e) => { if (!onSelect) return; e.preventDefault(); onSelect(e); }}
      >{body}</a>
    );
  } else if (onSelect) {
    hit = (
      <button type="button" className="cc-auth__hit" onClick={onSelect} aria-pressed={selected}>
        {body}
      </button>
    );
  } else {
    hit = (
      <div className="cc-auth__hit is-unlinked">
        {body}
        {unlinkedNote ? <span className="cc-auth__unlinked">{unlinkedNote}</span> : null}
      </div>
    );
  }

  return (
    <Tag
      className={cls}
      data-testid={testId}
      data-selected={selected ? '1' : '0'}
      {...(interactive === 'container' ? { onClick: onSelect } : null)}
      {...rest}
    >
      {hit}
      {footer}
    </Tag>
  );
}
